import useClickOutside from "@/hooks/useClickOutSide";
import React, { useEffect, useState } from "react";
import { MdArrowDropDown } from "react-icons/md";
import { SlArrowDown } from "react-icons/sl";
import { Center } from "../css";
import { LightText2 } from "../ui/Text";

interface Option {
  label: string;
  value: string | number;
}

interface SingleSelectProps {
  options: Option[];
  placeholder?: string;
  onChange: (value: Option) => void;
  selectedValue: Option;
  textClassname?: string;
  className?: string;
  dropdownClassName?: string;
  listClassName?: string;
  count?: number;
  containerClass?: string;
  openOnRender?: boolean;
  showSearch?: boolean;
  activeClass?: string;
  defaultValue?: Option;
  isDarkDropdown?: boolean;
}

const SingleSelect: React.FC<SingleSelectProps> = ({
  options,
  placeholder,
  onChange,
  selectedValue,
  textClassname = "text-text-primary",
  className = "",
  dropdownClassName = "",
  listClassName = "",
  containerClass = "",
  count,
  openOnRender = false,
  showSearch = false,
  activeClass = "font-black",
  defaultValue,
  isDarkDropdown = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useClickOutside(() => setIsOpen(false));
  const [search, setSearch] = useState("");
  const isPlaceholderSelected = selectedValue?.label === placeholder || (selectedValue as any) === placeholder;
  const DropdownIcon = isDarkDropdown ? MdArrowDropDown : SlArrowDown;

  const handleToggleDropdown = () => {
    setIsOpen((pre) => !pre);
  };

  const handleSelectOption = (value: Option) => {
    onChange(value);
    setSearch("");
    setIsOpen(false);
  };

  const filteredOptions = React.useMemo(() => {
    if (!search) return options;
    const searchTermLower = search.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(searchTermLower));
  }, [search, options]);

  useEffect(() => {
    if (openOnRender) {
      setIsOpen(true);
    }
  }, [openOnRender]);

  useEffect(() => {
    if (!selectedValue && defaultValue) {
      onChange(defaultValue);
    }
  }, [selectedValue, defaultValue]);

  return (
    <div className={`relative ${textClassname} ${containerClass}`} ref={dropdownRef}>
      <button
        className={`p-1.5 rounded-md flex items-center ${className}`}
        onClick={(e) => {
          e.preventDefault();
          handleToggleDropdown();
        }}
      >
        <span className={`truncate max-w-32 ${isPlaceholderSelected ? "!text-muted-foreground" : ""}`}>{selectedValue?.label || placeholder}</span>
        {count ? <span className=" px-0.5">{` (${count})`}</span> : null}
        <DropdownIcon className={`${isDarkDropdown ? "w-6 h-8" : " text-icon-default2"}  ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className={`absolute mt-1 w-fit bg-white border rounded-md shadow-lg whitespace-nowrap p-1 py-2 z-50 ${dropdownClassName}`}>
          {showSearch && (
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onFocus={() => setIsOpen(true)}
              placeholder={isOpen ? "Search..." : search}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              className={`px-3 py-[5px] mb-2 rounded-lg border border-fieldBorder hover:border-gray-400 focus:border-text-primary focus:border-text-primary-500 cursor-pointer flex justify-between items-center transition-all w-full duration-100 ease-in-out text-nowrap text-13 outline-none placeholder:text-gray-400 text-text-default`}
            />
          )}
          <ul className={`flex flex-col ${listClassName}`}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option.value}
                  className={`px-2 py-2 cursor-pointer hover:bg-primaryOpacity-10 hover:text-text-primary ${
                    option.value === selectedValue?.value ? activeClass : ""
                  }`}
                  onClick={() => handleSelectOption(option)}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <Center className="px-4 py-6">
                <LightText2 text="No options found" />
              </Center>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SingleSelect;
